import { useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { usePortal } from "@/context/PortalContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiError } from "@/lib/api";
import { AuthShell, AuthTabs, AuthLabel, AuthInput, AuthPrimaryButton, AuthOutlineButton, AUTH } from "@/components/portal/AuthShell";

export default function AuthPage() {
  const { portalKey, portal, config, categories } = usePortal();
  const { login, register } = useAuth();
  const { t, L } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const accountTypes = useMemo(() => {
    const base = config?.account_types?.length ? [...config.account_types] : [
      { key: "customer", role: "customer", label: L({ de: "Kundin / Kunde", en: "Customer" }), desc: L({ de: "Anbieter finden & anfragen", en: "Find & contact providers" }) },
      { key: "provider", role: "provider", label: L({ de: "Anbieter / KMU", en: "Provider / SME" }), desc: L({ de: "Eigenes Profil & Anfragen verwalten", en: "Manage your profile & inquiries" }) },
    ];
    const jobsOn = (config?.active_modules || []).includes("jobs");
    if (jobsOn && !base.find((a) => a.key === "candidate")) {
      base.push({
        key: "candidate", role: "candidate",
        label: config?.jobs?.candidate_label || L({ de: "Bewerber:in", fr: "Candidat·e", it: "Candidato/a", en: "Candidate" }),
        desc: L({ de: "Bewerberprofil erstellen & dich auf Stellen bewerben.", fr: "Créer un profil et postuler.", it: "Crea un profilo e candidati.", en: "Create a profile and apply to jobs." }),
      });
    }
    return base;
  }, [config, L]);

  const wantProvider = !!params.get("provider");
  const [mode, setMode] = useState(wantProvider ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [accountType, setAccountType] = useState(wantProvider ? "provider" : "");
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  const googleEnabled = config?.features?.google_auth === true;
  const selfReg = config?.features?.self_registration !== false;
  const effMode = selfReg ? mode : "login";
  const selected = accountTypes.find((a) => a.key === accountType);

  const canRegister = form.name.trim() && form.email.trim() && form.password.length >= 6 && accountType && terms;

  const submit = async (e) => {
    e.preventDefault();
    if (effMode === "register" && !terms) return toast.error(t("auth.termsRequired"));
    if (effMode === "register" && !accountType) return toast.error(t("auth.selectAccountType"));
    setBusy(true);
    try {
      if (effMode === "login") {
        await login(form.email, form.password);
        navigate(`/p/${portalKey}/dashboard`);
      } else {
        await register({ email: form.email, password: form.password, name: form.name, portal_key: portalKey, account_type: accountType, accepted_terms: true });
        // provider -> onboarding step 2, customer -> dashboard
        navigate(selected?.role === "provider" ? `/p/${portalKey}/onboarding` : `/p/${portalKey}/dashboard`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + `/p/${portalKey}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const chips = (categories || []).slice(0, 5).map((c) => L(c.name)).filter(Boolean);

  return (
    <AuthShell
      brandName={portal?.name || "KEWAGPortal"}
      heroKicker={L(config?.tagline)}
      heroTitle={L(config?.hero_title) || portal?.name}
      heroSubline={L(config?.hero_sub)}
      chips={chips}
      heroImage={config?.hero_image}
    >
      <div className="flex items-center justify-between mb-4">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs" style={{ color: AUTH.muted }} data-testid="auth-back">
          <ArrowLeft className="h-3.5 w-3.5" /> KEWAGPortal
        </Link>
        <span className="inline-flex items-center text-[11px] font-semibold uppercase tracking-[0.18em] px-3 py-1 rounded-full" style={{ border: "1px solid var(--accent)", color: "var(--accent)" }}>
          {portal?.name}
        </span>
      </div>

      <h2 className="font-heading text-3xl font-bold" style={{ color: AUTH.text }}>
        {effMode === "login" ? t("auth.welcome") : t("auth.createAccount")}
      </h2>
      <p className="mt-1.5 text-sm" style={{ color: AUTH.muted }}>{L(config?.tagline)}</p>

      {selfReg && <div className="mt-6"><AuthTabs mode={mode} onChange={setMode} testidPrefix="auth" /></div>}

      <form onSubmit={submit} className="mt-5 space-y-4">
        {effMode === "register" && (
          <>
            <div>
              <AuthLabel>{t("auth.accountType")}</AuthLabel>
              <div className="grid grid-cols-1 gap-2.5" data-testid="account-type-list">
                {accountTypes.map((a) => {
                  const on = accountType === a.key;
                  return (
                    <button key={a.key} type="button" data-testid={`account-type-${a.key}`} onClick={() => setAccountType(a.key)}
                      className="text-left rounded-xl p-3.5 transition-all flex items-start gap-3"
                      style={{ background: on ? "color-mix(in srgb, var(--accent) 14%, #0E1116)" : AUTH.inputBg, border: `1px solid ${on ? "var(--accent)" : AUTH.inputBorder}` }}>
                      <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full flex items-center justify-center" style={{ border: `2px solid ${on ? "var(--accent)" : AUTH.muted}`, background: on ? "var(--accent)" : "transparent" }}>
                        {on && <Check className="h-3 w-3" style={{ color: "var(--accent-fg)" }} />}
                      </span>
                      <span>
                        <span className="block text-sm font-semibold" style={{ color: AUTH.text }}>{L(a.label)}</span>
                        {a.desc && <span className="block text-xs mt-0.5" style={{ color: AUTH.muted }}>{L(a.desc)}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <AuthLabel>{t("auth.name")}</AuthLabel>
              <AuthInput data-testid="auth-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Max Muster" />
            </div>
          </>
        )}
        <div>
          <AuthLabel>{t("auth.email")}</AuthLabel>
          <AuthInput data-testid="auth-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@firma.ch" />
        </div>
        <div>
          <AuthLabel>{t("auth.password")}</AuthLabel>
          <AuthInput data-testid="auth-password" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
        </div>

        {effMode === "register" && (
          <label className="flex items-start gap-2.5 text-sm cursor-pointer" style={{ color: AUTH.text }}>
            <input type="checkbox" data-testid="auth-accept-terms" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[color:var(--accent)]" />
            <span className="text-xs leading-relaxed" style={{ color: AUTH.muted }}>{t("auth.acceptTerms")}</span>
          </label>
        )}

        <AuthPrimaryButton type="submit" disabled={busy || (effMode === "register" && !canRegister)} data-testid="auth-submit">
          {effMode === "login" ? t("auth.signIn") : t("auth.signUp")}
        </AuthPrimaryButton>
      </form>

      {googleEnabled && (
        <>
          <div className="my-5 flex items-center gap-3 text-xs" style={{ color: AUTH.muted }}>
            <div className="flex-1 h-px" style={{ background: AUTH.cardBorder }} /> {t("auth.or")} <div className="flex-1 h-px" style={{ background: AUTH.cardBorder }} />
          </div>
          <AuthOutlineButton onClick={google} data-testid="auth-google">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" /> {t("auth.continueGoogle")}
          </AuthOutlineButton>
        </>
      )}

      <p className="mt-6 text-center text-xs" style={{ color: AUTH.muted }}>
        {t("auth.teamHint")}{" "}
        <Link to="/control" className="font-medium" style={{ color: "var(--accent)" }} data-testid="auth-team-control">{t("auth.controlLogin")}</Link>
      </p>
    </AuthShell>
  );
}
